import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useRemoveRecentCallQuery } from '@frontend/api-call-intel';
import { useAppScopeStore } from '@frontend/scope';
import { contextFactory } from '@frontend/design-system';
import { PatientCallTrendsTabType } from '../types';

dayjs.extend(utc);

export interface CallIntelPatientInsightsContextValue {
  /**
   * The ID of the currently selected Patient Trends tab.
   */
  activeTab: PatientCallTrendsTabType;

  /**
   * Switch to a different Patient Trends tab.
   * @param tab the tab ID to select
   */
  setActiveTab: (tab: PatientCallTrendsTabType) => void;

  /**
   * Whether the Patient Trends panel is currently expanded.
   */
  showTrends: boolean;

  /**
   * Open or close the Patient Trends panel.
   * @param open `true` to show, `false` to hide
   */
  setShowTrends: (open: boolean) => void;

  /**
   * The date range for patient call trends.
   * - `start`: The start date is set to 12 months ago from the current date.
   * - `end`: The end date is set to the current date.
   * - Both dates are formatted as `YYYY-MM-DD HH:mm:ss.SSS`.
   */
  dateRange: { start: string; end: string };
}

export const [CallIntelPatientInsightsContext, useCallIntelPatientInsights] =
  contextFactory<CallIntelPatientInsightsContextValue>('useCallIntelPatientInsights must be used inside its Provider');

export const CallIntelPatientInsightsProvider: FC<PropsWithChildren<{ personId: string }>> = ({
  children,
  personId,
}) => {
  const { selectedLocationIds } = useAppScopeStore();

  const [activeTab, setActiveTab] = useState<PatientCallTrendsTabType>('sentiment-tab');
  const [showTrends, setShowTrends] = useState(false);

  // TODO: Milind Verify and cleanup this query remove strategy
  const { removeQuery } = useRemoveRecentCallQuery({
    locations: selectedLocationIds,
    personId,
  });

  const dateRange = useMemo(() => {
    const endUtc = dayjs().utc();
    const startUtc = endUtc.subtract(1, 'year'); // start = 12 months ago always

    return {
      start: startUtc.format('YYYY-MM-DD HH:mm:ss.SSS'),
      end: endUtc.format('YYYY-MM-DD HH:mm:ss.SSS'),
    };
  }, []);

  const value = useMemo(
    () => ({
      activeTab,
      setActiveTab,
      showTrends,
      setShowTrends,
      dateRange,
    }),
    [activeTab, showTrends, dateRange]
  );

  useEffect(() => {
    return () => {
      removeQuery();
    };
  }, []);
  return <CallIntelPatientInsightsContext.Provider value={value}>{children}</CallIntelPatientInsightsContext.Provider>;
};
